import { createAsyncThunk } from '@reduxjs/toolkit';
import { ObjectId } from 'app/common.types';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { sendMedia } from '../../../../services/mediaService/mediaService';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import { currentStoreIdSelector, selectStores } from '../../stores/ducks/stores.selector';
import { formatMultiplicatorToDTO } from '../../stores/utils';
import {
  changeOfferStatusApi,
  createLoyaltyModelApi,
  createOfferApi,
  getLoyaltyExtrasListApi,
  getLoyaltyModelDetailApi,
  getLoyaltyOffersApi,
  getLoyaltyProductListApi,
  patchLoyaltyCardNoteApi,
  updateLoyaltyModelApi,
  updateLoyaltyProductListApi,
  updateOfferApi,
} from '../api';
import { LoyaltyModelForm, LoyaltyOfferForm } from '../components/LoyaltyDialog';
import {
  isBadgeOfferMissingCategory,
  isOfferAmountInvalid,
  isProductLoyaltyType,
  isUnrestrictedZeroPointOffer,
  isUsageLimitInvalid,
  offerUsageLimitPayload,
} from '../loyalties.utils';
import { franchiseTimezone, redeemSlotsPayload } from '../offerRedeemSlots.utils';
import { offerSaveErrorKey } from '../offerSaveError';
import {
  FlatLoyaltyOfferDTO,
  LoyaltyCardUpdateDTO,
  LoyaltyModelEntity,
  LoyaltyModelWithoutOfferFormDTO,
  LoyaltyOfferDTO,
  LoyaltyOfferStatus,
  LoyaltyOfferType,
  LoyaltyProduct,
  LoyaltyType,
  PercentageLoyaltyOfferDTO,
  ProductLoyaltyOfferDTO,
  UpdateLoyaltyModelDTO,
} from '../type';
import { isPosLoyaltyType, offerTypeFields } from '../utils/offerType';
import { loyaltyModelSelector, loyaltyOffersSelector } from './loyalty.selector';
import { actions } from './loyalty.slice';

export const openNewLoyaltyDialog = (isOfferDialog: 'offer' | 'card' | 'both') => (dispatch: AppDispatch) => {
  dispatch(actions.openNewLoyaltyDialog(isOfferDialog));
};

export const openEditLoyaltyDialog =
  (isOfferDialog: 'offer' | 'card', loyaltyOffer?: LoyaltyOfferDTO) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const loyaltyModel = loyaltyModelSelector(getState());
    if (isOfferDialog === 'card' && loyaltyModel) {
      dispatch(actions.openEditLoyaltyDialog({ data: loyaltyModel, dialogType: isOfferDialog }));
    } else if (isOfferDialog === 'offer' && loyaltyOffer) {
      dispatch(actions.openEditLoyaltyDialog({ data: loyaltyOffer, dialogType: isOfferDialog }));
    }
  };

export const closeLoyaltyDialog = () => (dispatch: AppDispatch) => {
  dispatch(actions.closeLoyaltyDialog());
};

export const getLoyalties = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(actions.setIsLoading(true));
  try {
    const state = getState();
    const franchiseId = selectUserFranchise(state).id;
    const storeId = currentStoreIdSelector(state);
    if (!storeId) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.storeError'), variant: 'error' }));
      throw new Error('No store selected');
    }
    const [loyaltyModel, loyaltyOffers] = await Promise.all([
      getLoyaltyModelDetailApi(franchiseId, storeId),
      getLoyaltyOffersApi(storeId),
    ]);
    dispatch(actions.getLoyaltiesSuccess({ loyaltyModel, loyaltyOffers }));
  } catch (_e) {
    dispatch(actions.getLoyaltiesFailed());
  } finally {
    dispatch(actions.setIsLoading(false));
  }
};

export const getLoyaltyModel = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(actions.setIsLoading(true));
  try {
    const state = getState();
    const franchiseId = selectUserFranchise(state).id;
    const storeId = currentStoreIdSelector(state);
    if (!storeId) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.storeError'), variant: 'error' }));
      throw new Error('No store selected');
    }
    const loyaltyModel = await getLoyaltyModelDetailApi(franchiseId, storeId);
    dispatch(actions.getLoyaltyModelSuccess(loyaltyModel));
  } catch (_e) {
    dispatch(actions.getLoyaltyModelFailed());
  } finally {
    dispatch(actions.setIsLoading(false));
  }
};

export const getLoyaltyOffers = (storeId?: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.setIsOffersLoading(true));
  try {
    const loyaltyOffers = await getLoyaltyOffersApi(storeId);
    dispatch(actions.getLoyaltyOffersSuccess(loyaltyOffers));
  } catch (_e) {
    dispatch(actions.getLoyaltyOffersFailed());
  } finally {
    dispatch(actions.setIsOffersLoading(false));
  }
};

export const updateLoyaltyModel = (loyaltyForm: LoyaltyModelForm) => async (dispatch: any, getState: () => any) => {
  dispatch(actions.setLoyaltyDialogIsLoading(true));
  if (!loyaltyForm.id) {
    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.loyaltyCreationError'), variant: 'error' }));
    dispatch(actions.setLoyaltyDialogIsLoading(false));
    return;
  }
  if (!loyaltyForm.storeIds) {
    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.storeError'), variant: 'error' }));
    dispatch(actions.setLoyaltyDialogIsLoading(false));
    return;
  }
  if (!loyaltyForm.loyaltyType) {
    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.loyaltyTypeError'), variant: 'error' }));
    dispatch(actions.setLoyaltyDialogIsLoading(false));
    return;
  }

  const storeId = currentStoreIdSelector(getState());
  if (!storeId) {
    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.storeError'), variant: 'error' }));
    dispatch(actions.setLoyaltyDialogIsLoading(false));
    return;
  }

  const franchise = selectUserFranchise(getState());
  try {
    const logo =
      typeof loyaltyForm.franchiseLogo !== 'string'
        ? await sendMedia(loyaltyForm.franchiseLogo)
        : loyaltyForm.franchiseLogo;

    const loyaltyRequest: UpdateLoyaltyModelDTO = {
      id: loyaltyForm.id,
      franchiseName: loyaltyForm.franchiseName,
      franchiseId: franchise.id,
      franchiseLogo: logo,
      loyaltyType: loyaltyForm.loyaltyType,
      tagScanInterval: loyaltyForm.tagScanInterval,
      maxPoints: Number(loyaltyForm.maxPoints) || undefined,
      hasMaxPointsEnabled: !!loyaltyForm.maxPoints,
      blockPointsOnDiscount: !!loyaltyForm.blockPointsOnDiscount,
      posProductIds: loyaltyForm.posProductIds || [],
      _loyaltyOffers: loyaltyOffersSelector(getState()),
      multiplicators: formatMultiplicatorToDTO(loyaltyForm.multiplicators ?? []),
    };

    const promises: (Promise<LoyaltyModelEntity> | Promise<LoyaltyProduct[]>)[] = [
      updateLoyaltyModelApi(loyaltyRequest),
    ];
    if (isProductLoyaltyType(loyaltyForm.loyaltyType)) {
      promises.push(updateLoyaltyProductListApi(storeId, loyaltyForm.loyaltyProductList || []));
    }

    await Promise.all(promises);

    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.cardUpdateSuccess'), variant: 'success' }));
    dispatch(getLoyaltyModel());
    dispatch(actions.closeLoyaltyDialog());
  } catch (_e) {
    dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.cardUpdateError'), variant: 'error' }));
  } finally {
    dispatch(actions.setLoyaltyDialogIsLoading(false));
  }
};

export const getLoyaltyProductListThunk = createAsyncThunk(
  'loyalty/getLoyaltyProductList',
  async (_, { dispatch, getState }) => {
    try {
      const storeId = currentStoreIdSelector(getState() as RootState);
      if (!storeId) {
        throw new Error('No store selected');
      }
      const productList = await getLoyaltyProductListApi(storeId);
      dispatch(actions.getLoyaltyProductList(productList));

      const extrasList = await getLoyaltyExtrasListApi(storeId);
      dispatch(actions.getLoyaltyExtrasList(extrasList));
    } catch (_e) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.cardLoadingError'), variant: 'error' }));
    }
  },
);

export const createLoyaltyModelWithOffer =
  (loyaltyOfferForm: LoyaltyOfferForm, loyaltyModelForm: LoyaltyModelForm) =>
  async (dispatch: any, getState: () => RootState) => {
    dispatch(actions.setLoyaltyDialogIsLoading(true));
    if (!loyaltyModelForm.storeIds) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.storeError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (!loyaltyModelForm.loyaltyType) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.loyaltyTypeError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (
      isOfferAmountInvalid(loyaltyOfferForm.offerValue, loyaltyOfferForm.redeemPointAmount) ||
      isUsageLimitInvalid(loyaltyOfferForm.usageLimit)
    ) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.valueError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (isUnrestrictedZeroPointOffer(loyaltyOfferForm)) {
      dispatch(
        showMessage({ message: i18n.t('pages.loyalties.alertMessage.zeroPointUnrestricted'), variant: 'error' }),
      );
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (isBadgeOfferMissingCategory(loyaltyOfferForm)) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.badgeCategoryMissing'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (!loyaltyOfferForm.offerType) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.typeError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    if (loyaltyOfferForm.offerType === LoyaltyOfferType.PERCENTAGE && loyaltyOfferForm.offerValue > 100) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.percentageError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    const franchise = selectUserFranchise(getState());
    const storeIds = selectStores(getState()).map(store => store.id);
    try {
      const logo =
        typeof loyaltyModelForm.franchiseLogo !== 'string'
          ? await sendMedia(loyaltyModelForm.franchiseLogo)
          : loyaltyModelForm.franchiseLogo;

      const loyaltyRequest: LoyaltyModelWithoutOfferFormDTO = {
        affiliatedStoreIds: storeIds,
        franchiseName: loyaltyModelForm.franchiseName,
        franchiseLogo: logo,
        loyaltyType: loyaltyModelForm.loyaltyType,
        franchiseId: franchise.id,
        tagScanInterval: loyaltyModelForm.tagScanInterval,
        maxPoints: loyaltyModelForm.maxPoints,
      };

      const newOfferDTO: Partial<FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO> = {
        description: loyaltyOfferForm.description,
        redeemPointAmount: loyaltyOfferForm.isBadgeOffer ? 0 : Number(loyaltyOfferForm.redeemPointAmount),
        usageLimit: offerUsageLimitPayload(loyaltyOfferForm),
        requiredSegmentId: loyaltyOfferForm.isBadgeOffer ? loyaltyOfferForm.requiredSegmentId : null,
        storeListId: franchise.storeListId,
        type: loyaltyOfferForm.offerType,
      };

      Object.assign(newOfferDTO, offerTypeFields(loyaltyOfferForm, franchise.currencyCode));
      Object.assign(newOfferDTO, redeemSlotsPayload(loyaltyOfferForm.redeemSlots, franchiseTimezone(franchise)));
      // The card type may have been switched away from a POS one after products were picked.
      if (!isPosLoyaltyType(loyaltyModelForm.loyaltyType)) {
        newOfferDTO.loyaltyProducts = [];
        newOfferDTO.loyaltyExtras = [];
      }

      if (loyaltyOfferForm.image) {
        const image =
          typeof loyaltyOfferForm.image !== 'string' ? await sendMedia(loyaltyOfferForm.image) : loyaltyOfferForm.image;
        newOfferDTO.image = image;
      }

      const { id } = await createLoyaltyModelApi(loyaltyRequest);
      await createOfferApi(id, newOfferDTO);
      dispatch(getLoyalties());
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.cardSuccess'), variant: 'success' }));
      dispatch(actions.closeLoyaltyDialog());
    } catch (_e) {
      dispatch(actions.getLoyaltiesFailed());
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.cardError'), variant: 'error' }));
    } finally {
      dispatch(actions.setLoyaltyDialogIsLoading(false));
    }
  };

export const createLoyaltyOffer =
  (loyaltyModelId: LoyaltyModelEntity['id'], form: LoyaltyOfferForm) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(actions.setLoyaltyDialogIsLoading(true));
    if (isOfferAmountInvalid(form.offerValue, form.redeemPointAmount) || isUsageLimitInvalid(form.usageLimit)) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.valueError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (isUnrestrictedZeroPointOffer(form)) {
      dispatch(
        showMessage({ message: i18n.t('pages.loyalties.alertMessage.zeroPointUnrestricted'), variant: 'error' }),
      );
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (isBadgeOfferMissingCategory(form)) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.badgeCategoryMissing'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (form.offerType === LoyaltyOfferType.PERCENTAGE && form.offerValue > 100) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.percentageError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (!form.offerType) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.typeError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    const franchise = selectUserFranchise(getState());
    try {
      const newOfferDTO: Partial<FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO> = {
        description: form.description,
        redeemPointAmount: form.isBadgeOffer ? 0 : Number(form.redeemPointAmount),
        usageLimit: offerUsageLimitPayload(form),
        requiredSegmentId: form.isBadgeOffer ? form.requiredSegmentId : null,
        storeListId: form.storeListId || franchise.storeListId,
        type: form.offerType,
      };

      Object.assign(newOfferDTO, redeemSlotsPayload(form.redeemSlots, franchiseTimezone(franchise)));
      if (form.image) {
        const image = typeof form.image !== 'string' ? await sendMedia(form.image) : form.image;
        newOfferDTO.image = image;
      }

      Object.assign(newOfferDTO, offerTypeFields(form, franchise.currencyCode));

      const newOffer = await createOfferApi(loyaltyModelId, newOfferDTO);
      dispatch(actions.addOffer(newOffer));
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.offersSuccess'), variant: 'success' }));
      dispatch(actions.closeLoyaltyDialog());
    } catch (error: unknown) {
      dispatch(actions.getLoyaltiesFailed());
      dispatch(
        showMessage({
          message: i18n.t(offerSaveErrorKey(error, 'pages.loyalties.alertMessage.offersError')),
          variant: 'error',
        }),
      );
    } finally {
      dispatch(actions.setLoyaltyDialogIsLoading(false));
    }
  };

export const updateLoyaltyOffer =
  (loyaltyModelId: LoyaltyModelEntity['id'], form: LoyaltyOfferForm) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(actions.setLoyaltyDialogIsLoading(true));
    if (isOfferAmountInvalid(form.offerValue, form.redeemPointAmount) || isUsageLimitInvalid(form.usageLimit)) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.valueError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (isUnrestrictedZeroPointOffer(form)) {
      dispatch(
        showMessage({ message: i18n.t('pages.loyalties.alertMessage.zeroPointUnrestricted'), variant: 'error' }),
      );
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (isBadgeOfferMissingCategory(form)) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.badgeCategoryMissing'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (!form.offerType) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.typeError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }
    if (form.offerType === LoyaltyOfferType.PERCENTAGE && form.offerValue > 100) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.percentageError'), variant: 'error' }));
      dispatch(actions.setLoyaltyDialogIsLoading(false));
      return;
    }

    const franchise = selectUserFranchise(getState());
    try {
      const newOfferDTO: Partial<FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO> = {
        id: form.offerId,
        image: typeof form.image === 'string' ? form.image : undefined,
        description: form.description,
        redeemPointAmount: form.isBadgeOffer ? 0 : Number(form.redeemPointAmount),
        usageLimit: offerUsageLimitPayload(form),
        requiredSegmentId: form.isBadgeOffer ? form.requiredSegmentId : null,
        storeListId: form.storeListId || franchise.storeListId,
        type: form.offerType,
        value: undefined,
        currency: undefined,
      };
      Object.assign(newOfferDTO, offerTypeFields(form, franchise.currencyCode));
      Object.assign(newOfferDTO, redeemSlotsPayload(form.redeemSlots, franchiseTimezone(franchise)));
      if (form.image) {
        const image = typeof form.image !== 'string' ? await sendMedia(form.image) : form.image;
        newOfferDTO.image = image;
      }

      const updatedOffer = await updateOfferApi(loyaltyModelId, form.offerId, newOfferDTO);
      dispatch(actions.updateLoyaltyOffer(updatedOffer));
      dispatch(
        showMessage({ message: i18n.t('pages.loyalties.alertMessage.offersUpdateSuccess'), variant: 'success' }),
      );
      dispatch(actions.closeLoyaltyDialog());
    } catch (error: unknown) {
      dispatch(
        showMessage({
          message: i18n.t(offerSaveErrorKey(error, 'pages.loyalties.alertMessage.offersUpdateError')),
          variant: 'error',
        }),
      );
    } finally {
      dispatch(actions.setLoyaltyDialogIsLoading(false));
    }
  };

export const changeLoyaltyOfferStatus =
  (loyaltyModelId: string, loyaltyOfferId: string, status: LoyaltyOfferStatus) => async (dispatch: AppDispatch) => {
    try {
      const updatedOffer = await changeOfferStatusApi(loyaltyModelId, loyaltyOfferId, status);
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.offerStatusSuccess'), variant: 'success' }));
      dispatch(actions.updateLoyaltyOffer(updatedOffer));
    } catch (_e) {
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.offerStatusError'), variant: 'error' }));
    }
  };

export const patchLoyaltyCardNoteThunk = createAsyncThunk(
  'loyalty/patchLoyaltyCardNote',
  async (
    {
      userId,
      franchiseId,
      loyaltyCardId,
      loyaltyCardUpdate,
    }: { userId: ObjectId; franchiseId: ObjectId; loyaltyCardId: ObjectId; loyaltyCardUpdate: LoyaltyCardUpdateDTO },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await patchLoyaltyCardNoteApi(userId, franchiseId, loyaltyCardId, loyaltyCardUpdate);
    } catch (error: any) {
      if (error?.response?.status === 422 && error.response.data?.error?.status === 'NOTE_TOO_LONG') {
        dispatch(
          showMessage({ message: i18n.t('pages.loyalties.alertMessage.noteTooLongUpdateError'), variant: 'error' }),
        );
        return rejectWithValue(error);
      }
      dispatch(showMessage({ message: i18n.t('pages.loyalties.alertMessage.noteUpdateError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);
